/**
 * Shared element-builder base (SPEC §2.1 / §4.3): escapeHtml, parsedName,
 * tooltip markup, conditional-display attributes. Extracted mechanically from
 * the single-file port of v1 build_elements.js — every string is byte-for-byte
 * the v1 output, including the literal `undefined` tooltip text when
 * enableToolTip is true but toolTip is absent (research/03 §15.10). Do not
 * "fix" anything here without a BUGS.md entry.
 */

import { t, hasTranslations } from "../i18n/translate";
import { normalizeOperator } from "../pricing/conditional-operators";

/**
 * Translations v1: an option's VISIBLE text is swapped, while `data-label`
 * (the submitted value, a FormulaService pricing key) stays canonical. The
 * select's `.val()` is what conditional display compares, and with no `value`
 * attribute a browser answers with the option's text — so a translated option
 * also gets `value='<canonical label>'`. Untranslated options are emitted
 * exactly as before (no `value` attribute), keeping every existing golden.
 */
export function optionValueAttr(escapedLabel: string, translatedLabel: string): string {
  return hasTranslations() && translatedLabel !== escapedLabel
    ? " value='" + escapedLabel + "'"
    : "";
}

/**
 * ` dir="auto"` for a string the overlay actually swapped, and "" for one it
 * did not. Hebrew, Arabic and Farsi are right-to-left, and a label rendered
 * left-to-right reads as broken; `auto` lets the browser infer direction from
 * the first strong character, so there is no locale list to keep in step with
 * Shopify's. Emitted ONLY on translated text, so an untranslated store's markup
 * — and every characterization golden — is byte-identical to before.
 */
export function dirAttr(source: any): string {
  return hasTranslations() && typeof source === "string" && t(source) !== source
    ? ' dir="auto"'
    : "";
}

export { t };

export function escapeHtml(value: any): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// v1's per-builder `var parsedName = element.Name.replace(/"/g, "&quot;")` —
// the quote-only escape used for name="properties[…]" attributes
// (research/03 §15.9 escaping matrix).
export function parsedName(name: any): string {
  return name.replace(/"/g, "&quot;");
}

/**
 * Tooltip markup. v1 duplicates this expression in every builder with one of
 * two wrapper tags (research/03 §15.10): span-wrapped for number/slider/
 * lookup/text-display/image-upload/photo-editor/long-text, div-wrapped for
 * checkbox/radio/select-list/image-selector/calculation. `toolTip` is
 * interpolated raw — `undefined` renders literally.
 */
export function getToolTipHtml(element: any, wrapperTag: "span" | "div"): string {
  let enableToolTip =
    element.enableToolTip != undefined ? element.enableToolTip : false;
  // Translations v1: tooltip copy is shopper-visible text, so it is swapped
  // (identity when no translation exists — `undefined` still renders literally).
  let toolTip = enableToolTip ? t(element.toolTip) : null;
  if (enableToolTip !== true) return "";
  return wrapperTag === "span"
    ? "<span class='c-tooltip'><i class='fas fa-info-circle'></i><span class='right'>" +
        toolTip +
        "<i></i></span></span>"
    : "<div class='c-tooltip'><i class='fas fa-info-circle'></i><span class='right'>" +
        toolTip +
        "<i></i></span></div>";
}

export function getConditionalDisplay(element: any): any {
  var showElement = element.conditional_display === true ? false : true;
  var displayStyle = showElement === false ? "style='display:none;'" : "";
  var conditionalElement = element.conditional_display_selected_element;
  var conditionalElementValue =
    element.conditional_display_selected_element_value;
  // #209/#151: the operator attribute is emitted ONLY for a non-default operator — equals is the
  // default and stays absent, so every calculator saved before this shipped renders byte-identical
  // markup and the characterization goldens keep matching. Any unrecognised operator value falls
  // back to equals for the same reason.
  var conditionalOperator = normalizeOperator(element.conditional_display_operator);
  var conditionalOperatorAttr =
    conditionalOperator === "equals"
      ? ""
      : " data-conditionaloperator='" + conditionalOperator + "'";

  // #151: which element type the merchant picked as the trigger, recorded at save time. The
  // storefront resolves a number/slider controller ONLY when this says the condition meant one —
  // see pricing/conditional.ts. Absent on every condition saved before #151, and absent from the
  // markup entirely in that case, so existing calculators are byte-identical here too.
  var conditionalTriggerType = element.conditional_display_selected_element_type;
  var conditionalTriggerTypeAttr =
    typeof conditionalTriggerType === "string" && conditionalTriggerType !== ""
      ? " data-conditionaltriggertype='" + escapeHtml(conditionalTriggerType) + "'"
      : "";

  return {
    displayStyle: displayStyle,
    conditionalElement: conditionalElement,
    conditionalElementValue: conditionalElementValue,
    conditionalOperator: conditionalOperator,
    conditionalTriggerType: conditionalTriggerType ?? null,
    conditionalTemplate:
      displayStyle +
      " data-conditionalelement='" +
      conditionalElement +
      "' data-conditionalelementvalue='" +
      conditionalElementValue +
      "'" +
      conditionalOperatorAttr +
      conditionalTriggerTypeAttr,
  };
}

/**
 * Merchant "Width" setting (#181): 'half'/'third' opt into the side-by-side
 * layout classes in the app stylesheet. Absent/'full' = legacy markup.
 */
export function getElementWidthClass(element: any): string {
  if (element?.ElementWidth === "half") return " cpc-w-half";
  if (element?.ElementWidth === "third") return " cpc-w-third";
  return "";
}
